/**
 * CDP SDK News Plugin — URL→category taxonomy.
 *
 * Many news sites (e.g. 24chasa.bg) carry the article's section/category only in
 * the URL path, not in JSON-LD or `article:section` markup. The remote Site
 * Profile supplies an ordered list of rules; the first match wins.
 *
 * Pattern forms:
 *   - glob:  "/sport/*"          → matches the path with `*` as a wildcard
 *   - plain: "/biznes"           → prefix/substring match on the path
 *   - regex: "re:^/\\d+/sport/"  → tested against the path and full URL
 */

import type { UrlTaxonomyRule } from '../../core/types';

export interface ResolvedTaxonomy {
  category?: string;
  section?: string;
  contentType?: string;
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function matchRule(rule: UrlTaxonomyRule, path: string, url: string): boolean {
  const p = rule.pattern;
  if (!p) return false;

  if (p.startsWith('re:')) {
    try {
      const rx = new RegExp(p.slice(3));
      return rx.test(path) || rx.test(url);
    } catch {
      return false;
    }
  }

  if (p.includes('*')) {
    try {
      const rx = new RegExp('^' + p.split('*').map(escapeRegex).join('.*') + '$');
      return rx.test(path);
    } catch {
      return false;
    }
  }

  return path === p || path.startsWith(p) || path.includes(p);
}

/**
 * Resolve category/section/contentType from the first matching URL rule.
 * Returns an empty object when there are no rules or nothing matches.
 */
export function resolveTaxonomy(
  rules: UrlTaxonomyRule[] | undefined,
  path: string,
  url: string
): ResolvedTaxonomy {
  if (!rules || rules.length === 0) return {};
  for (const rule of rules) {
    if (matchRule(rule, path, url)) {
      return {
        category: rule.category,
        section: rule.section,
        contentType: rule.contentType,
      };
    }
  }
  return {};
}

/** Apply a slug→display-name map (case-insensitive) to a category value. */
export function applyCategoryMap(
  category: string | undefined,
  map: Record<string, string> | undefined
): string | undefined {
  if (!category) return category;
  if (!map) return category;
  const mapped = map[category] || map[category.toLowerCase()];
  return mapped || category;
}
